import { ChangeEvent, useState } from "react";

import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import {
  Field,
  HTMLWrapper,
  Input,
  Box,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "../question";

export const validate = async (
  answer: string,
  question: Question
): Promise<string[]> => {
  let schema = string();
  if (question.required) {
    schema = schema.required();
  }
  try {
    await schema.max(question.maxLength || 255).validate(answer);
    return [];
  } catch (err: any) {
    return err.errors;
  }
};

function SingleLineQuestion(props: Question): JSX.Element {
  const {
    id,
    question,
    required,
    maxLength,
    error,
    status,
    onChangeHandler,
    notes,
    hasMaxLength,
    existingValue
  } = props;
  const { translate } = useTranslator();
  const maxTextLength = maxLength || 255;

  const [inputText, setInputText] = useState(existingValue || "");

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setInputText(event.target.value);
    if (onChangeHandler) onChangeHandler(event.target.value);
  };

  return (
    <Field.Root
      my={4}
      w="full"
      id={`${id}`}
      invalid={error?.length > 0 || false}
      required={required || false}
      disabled={status === "saving"}
      className={error?.length > 0 ? "ehqQuestionError" : ""}
    >
      <Field.Label>
        <HTMLWrapper html={question} />
        {required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </Field.Label>
      <Input
        type="text"
        value={inputText}
        maxLength={maxTextLength}
        onChange={handleChange}
        size={{ base: "lg", lg: "md" }}
        fontSize="md !important"
        fontWeight="normal"
        data-testid="singleLineQuestion-input"
      />
      <Box
        display="grid"
        gridTemplateColumns="1fr auto"
        gap={4}
        w="full"
        mt={2}
      >
        <Field.HelperText color="gray.800" m={0}>
          {notes || ""}
        </Field.HelperText>
        {hasMaxLength && (
          <Field.HelperText color="gray.800" m={0} textAlign="right">
            {`${inputText.length}/${maxTextLength}`}
          </Field.HelperText>
        )}
      </Box>
      {error?.length > 0 && (
        <Field.ErrorText data-testid="singleLineQuestion-errorMessage">
          <Field.ErrorIcon />
          {translate(`errors.${error}`)}
        </Field.ErrorText>
      )}
    </Field.Root>
  );
}

export { SingleLineQuestion };
